import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Ticket, ChevronDown, ChevronUp, AlertTriangle, RefreshCw } from 'lucide-react';
import { PresetGroup, StatusPill, EmptyState, TableRowsSkeleton } from '../ds';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SYM = { INR: '₹', USD: '$', GBP: '£', EUR: '€', AUD: 'A$', CAD: 'C$', AED: 'AED ', SGD: 'S$' };
const PRODUCT_LABELS = {
  trial: 'Trial Class', gynae_consult: 'Gynae consult', nutritionist_consult: 'Nutritionist consult',
  elite_monthly: 'Elite', soulai_coach: 'SoulAI Coach', baby_tracker: 'Baby Tracker', garbhsanskar: 'Garbhsanskar',
  trial_class: 'Trial Class', consult: 'Consultations', all: 'All products',
  unscoped: 'Any product',
};

const fmtMoney = (byCur) => {
  const entries = Object.entries(byCur || {}).filter(([, v]) => v > 0);
  if (!entries.length) return '—';
  return entries.map(([c, v]) => `${SYM[c] || c + ' '}${Number(v).toLocaleString('en-IN')}`).join(' · ');
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

const RANGE_OPTIONS = [
  { key: '30', label: 'Last 30 days' }, { key: '90', label: 'Last 90 days' }, { key: '365', label: 'Last year' }, { key: 'all', label: 'All time' },
];
const rangeStart = (key) => {
  if (key === 'all') return null;
  const d = new Date(); d.setDate(d.getDate() - Number(key));
  return d.toISOString().slice(0, 10);
};

const RedemptionRows = ({ offerId, startDate }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams({ limit: 200 });
    if (startDate) params.set('start_date', startDate);
    axios.get(`${API_URL}/api/offers/${offerId}/redemptions?${params}`, { withCredentials: true })
      .then((r) => setRows(r.data.redemptions || [])).catch(() => setRows([]));
  }, [offerId, startDate]);
  if (rows === null) return <tr><td colSpan={8} className="px-4 py-3"><TableRowsSkeleton rows={2} cols={6} /></td></tr>;
  if (!rows.length) return <tr><td colSpan={8} className="px-4 py-4 text-xs text-slate-500 text-center" data-testid={`coupon-redemptions-empty-${offerId}`}>No redemptions in this window</td></tr>;
  return (
    <tr>
      <td colSpan={8} className="p-0 bg-slate-50/70">
        <table className="w-full text-xs" data-testid={`coupon-redemptions-${offerId}`}>
          <thead>
            <tr className="text-[11px] uppercase text-slate-500">
              <th className="text-left px-4 py-2">Redeemed</th><th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Product · Plan</th><th className="text-left px-3 py-2">Channel</th>
              <th className="text-right px-3 py-2">Paid</th><th className="text-right px-3 py-2">Discount</th><th className="text-left px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.redemption_id} className="border-t border-slate-200" data-testid={`coupon-redemption-row-${r.redemption_id}`}>
                <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{fmtDate(r.redeemed_at)}</td>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-800">{r.customer_name || '—'}</div>
                  <div className="text-slate-500">{r.customer_email || r.user_id || ''}</div>
                </td>
                <td className="px-3 py-2 text-slate-700">{PRODUCT_LABELS[r.product] || r.product || '—'}{r.plan ? ` · ${r.plan.replace('_', ' ')}` : ''}</td>
                <td className="px-3 py-2 text-slate-700">{r.channel_label}</td>
                <td className="px-3 py-2 text-right text-slate-800">{SYM[r.currency] || r.currency}{Number(r.amount || 0).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2 text-right text-emerald-700 font-medium">−{SYM[r.currency] || r.currency}{Number(r.discount_amount || 0).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                    <StatusPill status={r.payment_status || 'paid'} />
                    {r.coupon_check && r.coupon_check !== 'ok' && (
                      <span title={`OPS expected discount ${r.coupon_expected_discount}`} className="inline-flex items-center gap-1 text-[10px] text-amber-700" data-testid={`coupon-check-flag-${r.redemption_id}`}>
                        <AlertTriangle className="w-3 h-3" /> coupon {r.coupon_check}
                      </span>
                    )}
                    {r.price_check === 'mismatch' && (
                      <span title={`OPS catalogue expects ${r.price_expected}`} className="inline-flex items-center gap-1 text-[10px] text-rose-700" data-testid={`price-check-flag-${r.redemption_id}`}>
                        <AlertTriangle className="w-3 h-3" /> price mismatch
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </td>
    </tr>
  );
};

export const CouponUsagePanel = () => {
  const [range, setRange] = useState('90');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const startDate = rangeStart(range);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    axios.get(`${API_URL}/api/offers/usage?${params}`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => setData({ coupons: [], totals: {} })).finally(() => setLoading(false));
  };
  useEffect(load, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = data?.totals || {};
  const coupons = data?.coupons || [];
  return (
    <div className="card p-6 mt-8" data-testid="coupon-usage-section">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Ticket className="w-5 h-5 text-amber-600" />
            <h2 className="text-xl font-bold text-slate-900">Coupon Usage</h2>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            Every redemption from the App (LMS), website and sales links — same engine validates all channels. Click a row to see who used it.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PresetGroup options={RANGE_OPTIONS} value={range} onChange={setRange} testIdPrefix="coupon-range" />
          <button onClick={load} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Refresh" data-testid="coupon-usage-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="coupon-usage-totals">
        {[
          ['Redemptions', totals.redemptions ?? 0, 'coupon-total-redemptions'],
          ['Coupons used', totals.coupons_used ?? 0, 'coupon-total-coupons'],
          ['Discount given', fmtMoney(totals.discount_by_currency), 'coupon-total-discount'],
          ['Revenue with coupons', fmtMoney(totals.revenue_by_currency), 'coupon-total-revenue'],
        ].map(([label, value, tid]) => (
          <div key={tid} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
            <div className="text-lg font-bold text-slate-900 mt-0.5" data-testid={tid}>{value}</div>
          </div>
        ))}
      </div>

      {loading && !data ? <TableRowsSkeleton rows={3} cols={7} /> : coupons.length === 0 ? (
        <EmptyState icon={Ticket} title="No coupons yet" hint="Create one under Pricing → Edit → Discounts. Redemptions will appear here." testId="coupon-usage-empty" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm" data-testid="coupon-usage-table">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Coupon</th><th className="text-left px-3 py-2.5">Applies to</th>
                <th className="text-right px-3 py-2.5">Redemptions</th><th className="text-right px-3 py-2.5">Discount</th>
                <th className="text-right px-3 py-2.5">Revenue</th><th className="text-left px-3 py-2.5">Channels</th>
                <th className="text-left px-3 py-2.5">Last used</th><th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => {
                const key = c.offer_id || c.code;
                const isOpen = open === key;
                const limit = c.max_redemptions ? ` / ${c.max_redemptions}` : '';
                return (
                  <React.Fragment key={key}>
                    <tr className={`border-t border-slate-200 cursor-pointer hover:bg-amber-50/40 ${isOpen ? 'bg-amber-50/60' : ''}`}
                        onClick={() => setOpen(isOpen ? null : key)} data-testid={`coupon-usage-row-${c.code}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold text-slate-900">{c.code}</span>
                          <StatusPill status={c.is_active ? 'active' : 'cancelled'} label={c.is_active ? 'Active' : 'Inactive'} />
                          {c.mismatches > 0 && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-amber-700" data-testid={`coupon-mismatch-${c.code}`}>
                              <AlertTriangle className="w-3 h-3" /> {c.mismatches} check
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">{c.campaign_name}{c.currency ? ` · ${c.currency}` : ''}{c.end_date ? ` · till ${String(c.end_date).slice(0, 10)}` : ''}</div>
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-700">
                        {(c.applies_to || []).length ? c.applies_to.map((t) => PRODUCT_LABELS[t] || t).join(', ') : 'All products'}
                      </td>
                      <td className="px-3 py-3 text-right font-semibold text-slate-900" data-testid={`coupon-redemptions-count-${c.code}`}>
                        {c.redemptions}<span className="text-xs text-slate-400 font-normal"> (lifetime {c.lifetime_redemptions}{limit})</span>
                      </td>
                      <td className="px-3 py-3 text-right text-emerald-700 font-medium">{fmtMoney(c.discount_by_currency)}</td>
                      <td className="px-3 py-3 text-right text-slate-800">{fmtMoney(c.revenue_by_currency)}</td>
                      <td className="px-3 py-3 text-xs text-slate-700">
                        {Object.entries(c.by_channel || {}).map(([ch, n]) => `${(data.channel_labels || {})[ch] || ch} ${n}`).join(' · ') || '—'}
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-600 whitespace-nowrap">{fmtDate(c.last_redeemed_at)}</td>
                      <td className="px-3 py-3 text-slate-400">{isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</td>
                    </tr>
                    {isOpen && c.offer_id && <RedemptionRows offerId={c.offer_id} startDate={startDate} />}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
